import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
    Loader2,
    Clock,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';
import type { Schedule } from '@/stores/schedulerStore.js';
import { useSchedulerStore } from '@/stores/schedulerStore.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { useNavigate } from 'react-router-dom';
import { useServices } from '@/contexts/ServicesContext.js';

interface ScheduleCardProps {
    schedule: Schedule;
    isRunning?: boolean;
    onViewDetails?: (scheduleId: string) => void;
}

function formatCronExpression(cron: string): string {
    // Basic cron to human readable (simplified)
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    if (dayOfWeek === '1-5' && dayOfMonth === '*' && month === '*') {
        return `Weekdays at ${hour}:${minute.padStart(2, '0')}`;
    }
    if (dayOfWeek === '*' && dayOfMonth === '*' && month === '*') {
        if (hour === '*') {
            if (minute.startsWith('*/')) {
                return `Every ${minute.slice(2)} minutes`;
            }
            return `Every hour at :${minute.padStart(2, '0')}`;
        }
        return `Daily at ${hour}:${minute.padStart(2, '0')}`;
    }

    return cron;
}

export function ScheduleCard({
    schedule,
    isRunning,
    onViewDetails,
}: ScheduleCardProps) {
    const navigate = useNavigate();
    const { sessionService } = useServices();
    const { addTab, setActiveTab, getTab } = useChatTabsStore();

    // Use stable selector - get the logs array directly from the map
    const executionLogs = useSchedulerStore((state) => state.executionLogs.get(schedule.id));

    const hasError = !!schedule.lastError;
    const isDisabled = !schedule.enabled;

    // Get the most recent execution's session ID
    const latestLog = executionLogs?.[0];
    const latestSessionId = latestLog?.sessionId;
    const hasRecentExecution = latestLog && (Date.now() - (latestLog.completedAt || latestLog.triggeredAt)) < 3600000; // Within last hour

    const handleViewSession = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!latestSessionId) return;

        const tabId = `session-${latestSessionId}`;
        const existingTab = getTab(tabId);

        if (existingTab) {
            setActiveTab(tabId);
        } else {
            addTab({
                id: tabId,
                title: schedule.name,
                sessionId: latestSessionId,
                isPinned: false,
                status: isRunning ? 'running' : latestLog?.status === 'success' ? 'completed' : latestLog?.status === 'failed' ? 'failed' : undefined,
                scheduleId: schedule.id,
            });
        }

        sessionService.switchSession(latestSessionId);
        navigate(`/chat/${latestSessionId}`);
    };

    const getStatusColor = () => {
        if (isRunning) return 'text-yellow-500';
        if (hasError) return 'text-red-500';
        if (isDisabled) return 'text-muted-foreground';
        return 'text-green-500';
    };

    const getStatusIcon = () => {
        if (isRunning) return <Loader2 className="h-4 w-4 animate-spin" />;
        if (hasError) return <AlertTriangle className="h-4 w-4" />;
        if (isDisabled) return <AlertTriangle className="h-4 w-4" />;
        return <CheckCircle2 className="h-4 w-4" />;
    };

    return (
        <div
            className={cn(
                'p-3 rounded-lg border transition-all cursor-pointer',
                'hover:shadow-sm hover:border-primary/30',
                isRunning && 'border-yellow-400/50 bg-yellow-50 dark:bg-yellow-950/20',
                hasError && !isRunning && 'border-red-400/50 bg-red-50 dark:bg-red-950/10',
                isDisabled && 'opacity-60'
            )}
            onClick={() => onViewDetails?.(schedule.id)}
        >
            {/* Header: Status + Name */}
            <div className="flex items-start gap-2 mb-2">
                <div className={cn('mt-0.5 flex-shrink-0', getStatusColor())}>
                    {getStatusIcon()}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="font-medium text-sm truncate">{schedule.name}</div>
                        {isRunning && (
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400">
                                Running
                            </Badge>
                        )}
                        {/* View Session button for running or recent executions */}
                        {(isRunning || hasRecentExecution) && latestSessionId && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleViewSession}
                                className="h-5 px-2 text-[10px] gap-1 text-muted-foreground hover:text-foreground"
                            >
                                <ExternalLink className="h-3 w-3" />
                                View
                            </Button>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {formatCronExpression(schedule.cronExpression)}
                    </div>
                </div>
            </div>

            {/* Task preview */}
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2 pl-6">
                {schedule.task.instruction}
            </p>

            {/* Footer: Stats + Next run */}
            <div className="flex items-center justify-between pl-6">
                <div className="flex items-center gap-2">
                    {schedule.runCount > 0 && (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            {schedule.successCount}
                            {schedule.failureCount > 0 && (
                                <>
                                    <span className="text-muted-foreground">/</span>
                                    <XCircle className="h-3 w-3 text-red-500" />
                                    {schedule.failureCount}
                                </>
                            )}
                        </Badge>
                    )}
                </div>

                {schedule.enabled && schedule.nextRunAt && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>
                            {new Date(schedule.nextRunAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </span>
                    </div>
                )}
            </div>

            {/* Error message */}
            {hasError && (
                <div className="mt-2 p-2 rounded bg-red-100 dark:bg-red-950/30 text-xs text-red-700 dark:text-red-400 pl-6">
                    {schedule.lastError}
                </div>
            )}
        </div>
    );
}
